import { useFormik } from 'formik'
import { Loader } from 'lucide-react'
import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import * as Yup from 'yup'
import api from '../../../api'
import Responsive from '../../../assets/config/Responsive'
import Colors from '../../../assets/styles/Colors'
import { Container } from '../../../assets/styles/GlobalStyle'
import PageHeader from '../../../components/PageHeader'
import NavigationButton from '../../../components/v2/buttons/NavigationButton'
import Input from '../../../components/v2/Input'
import PhoneNumberInput from '../../../components/v2/PhoneNumberInput'
import Title from '../../../components/v2/Title'
import { RootState, useAppSelector } from '../../../store'
import { AppState } from '../../../store/appSlice'
import { AuthState, setCredentials } from '../../../store/authSlice'
import { IntTplState, setUserField } from '../../../store/internationalTplSlice'
import { getAuthToken, setAuthToken } from '../../../store/tokenStorage'
import { useInternationalTplPages } from './useInternationalTplPages'
import { useInternationalTplProgress } from './useInternationalTplProgress'

const InternationalTplDays = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { lang } = useAppSelector((root: { app: AppState }) => root.app)
  const auth = useSelector<RootState, AuthState>((state) => state.auth)
  const authTokenFromStorage = getAuthToken()
  const { data: pageInfo, isLoading: isPageInfoLoading } = api.useGetIntTplPageInfoQuery(
    'international-tpl-personal-info',
  )

  const { filter, checkout } = useAppSelector((data: { internationalTpl: IntTplState }) => data.internationalTpl)

  // Use the hook to get pages and page data from Redux
  const { pages: pagesFromHook, isLoading: isPagesLoading } = useInternationalTplPages()
  const { totalCount, passedCount, pages: progressPages } = useInternationalTplProgress()
  const displayPages = progressPages.length > 0 ? progressPages : pagesFromHook

  const [checkUser, { data, isLoading }] = api.useLoginOrRegisterMutation()

  const ValidationSchema = Yup.object({
    firstName: Yup.string().required(`${t('required')}`),
    lastName: Yup.string().required(`${t('required')}`),
    clientEmail: Yup.string().required(`${t('required')}`),
    clientPhone: Yup.string().required(`${t('required')}`),
    password: Yup.string().required(`${t('required')}`),
  })

  const initialValues = {
    firstName:
      checkout.user.clientType === 'legal'
        ? checkout.user.representativeName
        : checkout.user.translations?.en?.firstName || '',
    lastName:
      checkout.user.clientType === 'legal'
        ? checkout.user.representativeSurname
        : checkout.user.translations?.en?.lastName || '',
    clientEmail: checkout.user.clientEmail || '',
    clientPhone: checkout.user.clientPhone || '',
    password: checkout.user.password || '',
  }

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: ValidationSchema,
    enableReinitialize: true,
    validateOnMount: true,
    onSubmit: () => {},
  })

  const checkUserCallback = useCallback(async () => {
    if (auth && !auth.user && !auth.token && !auth.userLoading && formik.isValid) {
      try {
        await checkUser({
          firstName: initialValues.firstName || '',
          lastName: initialValues.lastName || '',
          email: checkout.user.clientEmail!,
          personalNumber: checkout.user.idNumber,
          phone: checkout.user.clientPhone!,
          password: checkout.user.password!,
        }).unwrap()
      } catch (error: any) {
        const errors = error?.data?.errors

        if (Array.isArray(errors) && errors.length > 0) {
          const { code, message } = errors[0]

          switch (code) {
            case 'PHONE_ALREADY_EXISTS':
              toast.error(`${t('phoneAlreadyExist')}`)
              break

            case 'PERSONAL_NUMBER_ALREADY_EXISTS':
              toast.error(`${t('personalNumberAlreadyExist')}`)
              break

            case 'INVALID_CREDENTIALS':
              toast.error(`${t('incorrect_credentials')}`)
              break

            default:
              toast.error(message || 'მოხდა შეცდომა, გთხოვთ სცადოთ მოგვიანებით')
              break
          }
        } else {
          toast.error('მოხდა შეცდომა. გთხოვთ სცადოთ მოგვიანებით... (checkUserCallback)')
        }
      }
    }
  }, [
    checkUser,
    t,
    auth,
    checkout.user.clientEmail,
    checkout.user.clientPhone,
    checkout.user.idNumber,
    checkout.user.password,
    formik.isValid,
    initialValues.firstName,
    initialValues.lastName,
  ])

  const _saveUserCallback = useCallback(() => {
    if (auth && !auth.user && !authTokenFromStorage && data && data.user && data.token) {
      return [
        dispatch(
          setCredentials({
            user: data.user,
            token: data.token,
          }),
        ),
        setAuthToken(data.token),
        navigate('/international-tpl-offer'),
      ]
    }
  }, [authTokenFromStorage, auth, data, navigate, dispatch])

  useEffect(() => {
    _saveUserCallback()
  }, [_saveUserCallback])

  // auto redirect first page
  useEffect(() => {
    if (!filter?.isoCode) {
      navigate('/international-tpl-country')
    }
    return () => {}
  }, [filter?.isoCode, navigate])

  return isLoading || isPageInfoLoading || isPagesLoading ? (
    <Loader />
  ) : (
    <Wrapper>
      <PageHeader total={totalCount} passed={passedCount} pages={displayPages} />
      <Container>
        <Content>
          <Title text={pageInfo?.translations?.[lang]?.title} />
          <Block>
            <Input
              id="email"
              name="email"
              label={`${t('email')}`}
              type="email"
              placeholder=""
              value={checkout.user.clientEmail || ''}
              onChange={(value: string) => {
                dispatch(
                  setUserField({
                    fieldName: 'clientEmail',
                    fieldValue: value,
                  }),
                )
                formik.setFieldValue('clientEmail', value)
              }}
              disabled={!!checkout.user.clientEmail}
              width={'fullWidth'}
              error={formik.errors.clientEmail && checkout.user.clientEmail ? true : false}
              errorText={formik.errors.clientEmail}
              onBlur={formik.handleBlur}
            />

            <PhoneNumberInput
              id="phone-number"
              hideButtons={true}
              label={`${t('phone')}`}
              value={checkout.user.clientPhone || ''}
              onChange={(value, event) => {
                dispatch(
                  setUserField({
                    fieldName: 'clientPhone',
                    fieldValue: value,
                  }),
                )
                dispatch(
                  setUserField({
                    fieldName: 'clientPhoneCountryCode',
                    fieldValue: event?.dialCode,
                  }),
                )
                formik.setFieldValue('clientPhone', value)
              }}
              disabled={!!checkout.user.clientPhone}
              error={formik.errors.clientPhone && checkout.user.clientPhone ? true : false}
              errorText={formik.errors.clientPhone}
              onBlur={formik.handleBlur}
            />

            <Input
              id="password"
              name="password"
              label={`${t('password')}`}
              type="password"
              placeholder=""
              value={checkout.user.password || ''}
              onChange={(value: string) => {
                dispatch(
                  setUserField({
                    fieldName: 'password',
                    fieldValue: value,
                  }),
                )
                formik.setFieldValue('password', value)
              }}
              width={'fullWidth'}
              error={formik.errors.password && checkout.user.password ? true : false}
              errorText={formik.errors.password}
              onBlur={formik.handleBlur}
              icon
            />
          </Block>

          <Buttons>
            <NavigationButton
              text={`${t('back')}`}
              buttonType="prev"
              onClick={() => navigate('/international-tpl-car-info')}
            />
            <NavigationButton
              text={`${t('authorization')}`}
              buttonType="next"
              disabled={!formik.isValid}
              onClick={() => {
                if (auth && auth.user && auth.token) {
                  navigate('/international-tpl-offer')
                } else checkUserCallback()
              }}
            />
          </Buttons>
        </Content>
      </Container>
    </Wrapper>
  )
}

const Content = styled.div`
  margin-top: 40px;
  margin-bottom: 32px;
  z-index: 2;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;

  ${Responsive.mobile} {
    margin-top: 24px;
  }
`
const Block = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  max-width: 432px;
  width: 100%;
  margin: 0 auto;
  box-shadow: 0 4px 50px 0 rgba(0, 0, 0, 0.04);
  background-color: ${Colors.white};
  border-radius: 8px;
  padding: 20px 32px;

  .icon-days {
    position: absolute;
    top: 61px;
    font-size: 16px;
    color: ${Colors.primaryBlack};
    user-select: none;
  }

  ${Responsive.mobile} {
    margin-top: 24px;

    .icon-days {
      top: 62px;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }
`
const Wrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: column;
  background-color: ${Colors.backgroundGrey};
  padding-bottom: 20px;
  min-height: 100vh;

  .react-datepicker-wrapper {
    width: 100%;
  }
  .react-datepicker__input-container {
    user-select: none;
    pointer-events: none;
    cursor: default !important;
    input:focus {
      caret-color: transparent;
    }
  }
`

const Buttons = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-inline: auto;
  width: 100%;
  margin-top: 48px;
  max-width: 368px;

  button {
    width: 100%;
    padding-inline: 0px;
    &:first-child {
      margin-right: 20px;
    }
  }

  ${Responsive.mobile} {
    max-width: 296px;
    margin-top: 40px;
    button {
      &:first-child {
        margin-right: 15px;
      }
    }
  }
`

export default InternationalTplDays
